import dayjs from 'dayjs'
import html2pdf from 'html2pdf.js'

const formatDate = (date) => {
  if (!date) return '____년 __월 __일'
  try {
    return dayjs(date).format('YYYY년 MM월 DD일')
  } catch {
    return '____년 __월 __일'
  }
}

const formatCurrency = (amount) => {
  if (amount === undefined || amount === null || amount === '') return ''
  try {
    const normalized =
      typeof amount === 'string'
        ? parseInt(amount.replace(/,/g, ''), 10)
        : Number(amount)
    if (Number.isNaN(normalized)) return ''
    return normalized.toLocaleString()
  } catch {
    return ''
  }
}

// 연봉 → 월급 환산 (정수 나눗셈, 입력이 없거나 이상하면 빈 문자열)
const getMonthlySalaryFromAnnual = (annual) => {
  if (annual === undefined || annual === null || annual === '') return ''
  try {
    const normalized =
      typeof annual === 'string'
        ? parseInt(annual.replace(/,/g, ''), 10)
        : Number(annual)
    if (Number.isNaN(normalized)) return ''
    return Math.floor(normalized / 12)
  } catch {
    return ''
  }
}

const getCompanyInfo = (data) => ({
  companyName: data?.companyName || '주식회사 하우투약',
  companyPhone: data?.companyPhone || '02-555-0590',
  companyAddress: data?.companyAddress || '서울시 강남구 테헤란로 501, 5층',
  representativeName: data?.representativeName || '김경열',
})

// 섹션 번호 계산 유틸
const getSectionNumberOffset = (laborContractType) =>
  laborContractType === 'CONVERSION' ? 1 : 0

const buildSectionLabels = (laborContractType) => {
  const offset = getSectionNumberOffset(laborContractType)

  return {
    workplace: 2 + offset, // 근무장소
    jobContent: 3 + offset, // 업무의 내용
    workingHours: 4 + offset, // 소정근로시간
    workingDays: 5 + offset, // 근무일 / 휴일
    wage: 6 + offset, // 임금
    annualLeave: 7 + offset, // 연차 유급 휴가
    insurance: 8 + offset, // 사회보험 적용여부
    delivery: 9 + offset, // 근로계약서 교부
    etc: 10 + offset, // 기타
  }
}

// 근로계약서 내부에서 사용할 실제 계약 타입 결정
// - 백엔드 contractType: 'SALARY' | 'EMPLOYMENT' 등
// - snapshot.workType: 'FULL_TIME' | 'CONTRACT' | 'CONVERSION' 등
//   → EMPLOYMENT 인 경우 snapshot.workType을 기준으로 실제 타입을 정규화
const resolveLaborContractType = (contractType, workType) => {
  // 근로계약서(EMPLOYMENT) 인 경우 snapshot.workType 우선 사용
  if (contractType === 'EMPLOYMENT') {
    if (
      workType === 'CONTRACT' ||
      workType === 'CONVERSION' ||
      workType === 'FULL_TIME'
    ) {
      return workType
    }
    // 알 수 없는 값이면 정규직으로 처리
    return 'FULL_TIME'
  }

  // 이미 FULL_TIME / CONTRACT / CONVERSION 으로 넘어오는 경우 그대로 사용
  if (
    contractType === 'CONTRACT' ||
    contractType === 'CONVERSION' ||
    contractType === 'FULL_TIME'
  ) {
    return contractType
  }

  // 기본은 정규직
  return 'FULL_TIME'
}

// 타입별 타이틀 반환
const getContractTitle = (laborContractType) => {
  switch (laborContractType) {
    case 'CONTRACT':
      return '근로 계약서 (계약직)'
    case 'CONVERSION':
      return '근로 계약서 (계약직)'
    case 'FULL_TIME':
    default:
      return '근로 계약서'
  }
}

// 타입별 근로계약기간 섹션 반환
const getContractPeriodSection = (laborContractType, data) => {
  const startDate = formatDate(data?.contractStartDate)
  const endDate = formatDate(data?.contractEndDate)

  switch (laborContractType) {
    case 'CONTRACT':
      // 계약직: 시작일부터 종료일까지
      return `
                <section>
                  <div style="font-size: 13px; font-weight: 700; margin: 0 0 10px 0; letter-spacing: -0.3px; color: #000;">1. 근로계약기간</div>
                  <p style="font-size: 12px; margin: 0; line-height: 1.8; color: #383E44; font-weight: 400; letter-spacing: -0.3px;">
                    · <span style="text-decoration: underline;">${startDate}</span>부터 <span style="text-decoration: underline;">${endDate || '____년 __월 __일'}</span>까지
                  </p>
                  <p style="font-size: 12px; margin: 0; line-height: 1.8; color: #383E44; font-weight: 400; letter-spacing: -0.3px;"> · 계약 기간 만료 시 본 계약은 자동 종료되며, 갱신을 보장하지 않는다. </p>
                </section>
              `
    case 'CONVERSION':
      // 정규직 전환용: 시작일부터 (전환 관련 내용)
      return `
                <section>
                  <div style="font-size: 13px; font-weight: 700; margin: 0 0 10px 0; letter-spacing: -0.3px; color: #000;">1. 근로계약기간</div>
                  <p style="font-size: 12px; margin: 0; line-height: 1.8; color: #383E44; font-weight: 400; letter-spacing: -0.3px;">
                    · <span style="text-decoration: underline;">${startDate}</span>부터 <span style="text-decoration: underline;">${endDate || '____년 __월 __일'}</span>까지
                  </p>
                    <p style="font-size: 12px; margin: 0; line-height: 1.8; color: #383E44; font-weight: 400; letter-spacing: -0.3px;"> · 본 계약은 계약 기간 만료 시 본 계약은 자동 종료되며, 계약 기간 만료는 해고에 해당하지 않는다.</p>
                  </p>
                </section>
              `
    case 'FULL_TIME':
    default:
      // 정규직: 시작일부터 (종료일 없음)
      return `
                <section>
                  <div style="font-size: 13px; font-weight: 700; margin: 0 0 10px 0; letter-spacing: -0.3px; color: #000;">1. 근로계약기간</div>
                  <p style="font-size: 12px; margin: 0; line-height: 1.8; color: #383E44; font-weight: 400; letter-spacing: -0.3px;">
                    · <span style="text-decoration: underline;">${startDate}</span>부터</p>
                </section>
              `
  }
}

// 근로 계약서 템플릿
const buildLaborContractHtml = (data) => {
  const { companyName, companyPhone, companyAddress, representativeName } =
    getCompanyInfo(data)

  // 템플릿에서 받은 발급 번호를 그대로 사용 (날짜 기반 생성 제거)
  const issueNumber = data?.certificateNo || '____'

  // 백엔드 contractType(EMPLOYMENT) + workType을 기반으로
  // 근로계약서에서 사용할 실제 타입(FULL_TIME / CONTRACT / CONVERSION) 계산
  const rawContractType = data?.contractType
  const workType = data?.workType
  const laborContractType = resolveLaborContractType(rawContractType, workType)

  // 섹션 라벨 번호 계산
  const sectionLabels = buildSectionLabels(laborContractType)

  // 정규직 전환(CONVERSION)일 때만 2번 섹션 추가
  const conversionSection =
    laborContractType === 'CONVERSION'
      ? `
                <section>
                  <div style="font-size: 13px; font-weight: 700; margin: 0 0 10px 0; letter-spacing: -0.3px; color: #000;">2. 정규직 전환 관련 사항</div>
                  <p style="font-size: 12px; margin: 0; line-height: 1.8; color: #383E44; font-weight: 400; letter-spacing: -0.3px;">
                    · 본 계약은 근로자의 직무 적합성, 업무 수행 능력 및 조직 적응도 등을 평가하기 위한 기간제 근로계약이다.
                  </p>
                  <p style="font-size: 12px; margin: 0; line-height: 1.8; color: #383E44; font-weight: 400; letter-spacing: -0.3px;">
                    · 계약기간 종료 후 정규직 전환 여부는 회사의 평가 및 경영상 판단에 따라 결정된다.
                  </p>
                  <p style="font-size: 12px; margin: 0; line-height: 1.8; color: #383E44; font-weight: 400; letter-spacing: -0.3px;">
                    · 본 계약은 정규직 전환 또는 계약 갱신을 보장하지 않는다.
                  </p>
                </section>
              `
      : ''

  // 타입별 타이틀과 근로계약기간 섹션 가져오기
  const contractTitle = getContractTitle(laborContractType)
  const contractPeriodSection = getContractPeriodSection(
    laborContractType,
    data,
  )

  return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              margin: 0;
              padding: 0;
              font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, sans-serif;
              color: #191a1c;
              line-height: 1.6;
              width: 210mm;
              height: auto;
              min-height: auto;
              max-height: 297mm;
              background: white;
              overflow: visible;
              page-break-inside: avoid !important;
              page-break-after: avoid !important;
              page-break-before: avoid !important;
            }
            @page {
              size: A4;
              margin: 0;
            }
            div, table, tr, td, p {
              page-break-inside: avoid !important;
            }
            .page {
              page-break-after: always;
              page-break-inside: avoid;
            }
            .page:last-child {
              page-break-after: auto;
            }
          </style>
        </head>
        <body style="letter-spacing: -0.03em; margin: 0; padding: 0; height: auto; max-height: 297mm; overflow: visible;">
          <!-- 첫 번째 페이지 -->
          <div class="page" style="position: relative; width: 210mm; height: 297mm; background: white; box-sizing: border-box; page-break-after: always; page-break-inside: avoid; display: block; overflow: hidden;">
            <!-- 장식 헤더 -->
            <div style="position: relative; width: 100%; height: 26px; overflow: hidden;">
              <!-- 파란색 영역 -->
              <div style="position: absolute; left: 0; top: 0; width: calc(86% - 0.5px); height: 100%; background: #007AFF; clip-path: polygon(100% 0, 0 0, 0 100%, calc(100% - 26px) 100%);"></div>
              <!-- 회색 영역 (우측) -->
              <div style="position: absolute; right: 0; top: 0; width: 16%; height: 100%; background: #EEF3F6; clip-path: polygon(100% 0, 26px 0, 0 100%, 100% 100%)"></div>
            </div>

            <div style="padding: 20mm 20mm ${
              laborContractType === 'CONVERSION' ? '0mm' : '20mm'
            } 20mm; letter-spacing: -0.03em; display: flex; flex-direction: column; height: calc(297mm - 26px - ${
              laborContractType === 'CONVERSION' ? '20mm' : '40mm'
            }); max-height: calc(297mm - 26px - 40mm); box-sizing: border-box; overflow: hidden;">
              <div style="position: relative; margin-bottom: 35px; flex-shrink: 0;">
                <div style="color: #000; font-size: 24px; font-weight: 700; margin: 0; text-align: center;">${contractTitle}</div>
                <div style="position: absolute; right: 0; top: 50%; transform: translateY(-50%); font-size: 10px; color: #000; letter-spacing: -0.25px; font-weight: 600;">발급 번호: <span style="font-weight: 400;">${issueNumber}</span></div>
              </div>

              <div style="margin-bottom: 35px; flex-shrink: 0;">
                <div style="color: #000; font-size: 12px; font-weight: 600; margin: 0; letter-spacing: -0.3px; line-height: 160%; max-width: 100%; word-wrap: break-word;">
                  ${companyName} (이하 "사업주"라 함)과 ${
                    data?.employeeName || '____'
                  } (이하 "근로자"라 함)은(는) 다음과 같이 ${
                    laborContractType === 'FULL_TIME'
                      ? '근로계약을 체결한다.'
                      : '기간제 근로계약을 체결한다.'
                  }
                </div>
              </div>

              <div style="display: flex; flex-direction: column; justify-content: space-between; flex: 1; min-height: 0;">
                <!-- 1. 근로계약기간 -->
                ${contractPeriodSection}

                <!-- 2. 정규직 전환 관련 사항 (CONVERSION일 때만) -->
                ${conversionSection}

                <!-- 2. 근무장소 -->
                <section>
                  <div style="font-size: 13px; font-weight: 700; margin: 0 0 10px 0; letter-spacing: -0.3px; color: #000;">${
                    sectionLabels.workplace
                  }. 근무장소</div>
                  <p style="font-size: 12px; margin: 0; line-height: 1.8; color: #383E44; font-weight: 400; letter-spacing: -0.3px;">
                    · ${data?.workplace || companyAddress}
                  </p>
                </section>

                <!-- 3. 업무의 내용 -->
                <section>
                  <div style="font-size: 13px; font-weight: 700; margin: 0 0 10px 0; letter-spacing: -0.3px; color: #000;">${
                    sectionLabels.jobContent
                  }. 업무의 내용</div>
                  <p style="font-size: 12px; margin: 0; line-height: 1.8; color: #383E44; font-weight: 400; letter-spacing: -0.3px;">
                    · ${
                      data?.jobDescription ||
                      data?.position ||
                      data?.rank ||
                      data?.title ||
                      '서비스 기획'
                    }
                  </p>
                </section>

                <!-- 4. 소정근로시간 -->
                <section>
                  <div style="font-size: 13px; font-weight: 700; margin: 0 0 10px 0; letter-spacing: -0.3px; color: #000;">${
                    sectionLabels.workingHours
                  }. 소정근로시간</div>
                  <p style="font-size: 12px; margin: 0; line-height: 1.8; color: #383E44;">
                    · ${data?.workStartTime || '09'}시 ${
                      data?.workStartMinute || '30'
                    }분부터 ${data?.workEndTime || '18'}시 ${
                      data?.workEndMinute || '30'
                    }분까지 (휴게 시간 : ${
                      data?.breakStartTime || '12'
                    }시 ${data?.breakStartMinute || '30'}분 ~ ${
                      data?.breakEndTime || '13'
                    }시 ${data?.breakEndMinute || '30'}분)
                  </p>
                </section>

                <!-- 5. 근무일 / 휴일 -->
                <section>
                  <div style="font-size: 13px; font-weight: 700; margin: 0 0 10px 0; letter-spacing: -0.3px; color: #000;">${
                    sectionLabels.workingDays
                  }. 근무일 / 휴일</div>
                  <p style="font-size: 12px; margin: 0; line-height: 1.8; color: #383E44;">
                    · ${data?.workdays || '매주 5일 (또는 매일단위)근무'}, ${
                      data?.holidays || '주휴일 매주 일요일'
                    }
                  </p>
                </section>

                <!-- 6. 임금 -->
                <section>
                  <div style="font-size: 13px; font-weight: 700; margin: 0 0 10px 0; letter-spacing: -0.3px; color: #000;">${
                    sectionLabels.wage
                  }. 임금</div>
                  <p style="font-size: 12px; margin: 0; line-height: 1.8; color: #383E44;">
                    · 월(일, 시간급) : ${
                      formatCurrency(
                        getMonthlySalaryFromAnnual(data?.basicSalary),
                      ) || '000,000,000'
                    }원 (연봉 ${
                      data?.basicSalary
                        ? formatCurrency(data.basicSalary)
                        : '000,000,000'
                    }원)
                  </p>
                  <p style="font-size: 12px; margin: 0; line-height: 1.8; color: #383E44;">
                    · 기타수당(세부 등) : 있음 (✓), 없음 ( )
                  </p>
                  <p style="font-size: 12px; margin: 0; line-height: 1.8; color: #383E44;">
                    · 식대 : ${
                      data?.mealAllowance
                        ? `${formatCurrency(data.mealAllowance)}원`
                        : '200,000원'
                    }
                  </p>
                  <p style="font-size: 12px; margin: 0; line-height: 1.8; color: #383E44;">
                    · 임금지급일 : 매월(매주 또는 매일) ${
                      data?.paymentStandard || '05'
                    }일 (휴일의 경우는 전일 지급)
                  </p>
                  <p style="font-size: 12px; margin: 0; line-height: 1.8; color: #383E44;">
                    · 지급방법 : 근로자에게 직접 지급 ( ), 근로자 명의 예금통장에 입금 (✓)
                  </p>
                </section>

                <!-- 7. 연차 유급 휴가 -->
                <section>
                  <div style="font-size: 13px; font-weight: 700; margin: 0 0 10px 0; letter-spacing: -0.3px; color: #000;">${
                    sectionLabels.annualLeave
                  }. 연차 유급 휴가</div>
                  <p style="font-size: 12px; margin: 0; line-height: 1.8; color: #383E44;">
                    · 연차유급휴가는 근로기준법에서 정하는 바에 따라 부여함
                  </p>
                </section>

                <!-- 8. 사회보험 적용여부 -->
                <section>
                  <div style="font-size: 13px; font-weight: 700; margin: 0 0 10px 0; letter-spacing: -0.3px; color: #000;">${
                    sectionLabels.insurance
                  }. 사회보험 적용여부 (해당란에 체크)</div>
                  <div style="display: flex; gap: 24px; flex-wrap: wrap; font-size: 12px; color: #383E44;">
                    <label style="display: flex; align-items: center; gap: 6px;">
                      <input type="checkbox" ${
                        data?.employmentInsurance ? 'checked' : ''
                      } disabled style="width: 14px; height: 14px;" />
                      고용보험
                    </label>
                    <label style="display: flex; align-items: center; gap: 6px;">
                      <input type="checkbox" ${
                        data?.industrialAccidentInsurance ? 'checked' : ''
                      } disabled style="width: 14px; height: 14px;" />
                      산재보험
                    </label>
                    <label style="display: flex; align-items: center; gap: 6px;">
                      <input type="checkbox" ${
                        data?.nationalPension ? 'checked' : ''
                      } disabled style="width: 14px; height: 14px;" />
                      국민연금
                    </label>
                    <label style="display: flex; align-items: center; gap: 6px;">
                      <input type="checkbox" ${
                        data?.healthInsurance ? 'checked' : ''
                      } disabled style="width: 14px; height: 14px;" />
                      건강보험
                    </label>
                  </div>
                </section>
              </div>
            </div>
            
            <!-- 하우투약 푸터 -->
            <div style="position: absolute; bottom: 20mm; left: 0; right: 0; display: flex; justify-content: center; align-items: center; gap: 8px; font-size: 10px; color: #383E44;">
              <img src="/assets/icon/aops/howtoyak-logo.png" style="height: 16px; width: auto; display: block;" />
              <span style="font-weight: 600; letter-spacing: 0.08em;">HOW TO YAK</span>
            </div>
          </div>

          <!-- 두 번째 페이지 -->
          <div class="page" style="position: relative; width: 210mm; height: auto; min-height: 297mm; background: white; box-sizing: border-box; page-break-before: always; page-break-inside: avoid; display: block;">
            <!-- 장식 헤더 -->
            <div style="position: relative; width: 100%; height: 26px; overflow: hidden;">
              <!-- 파란색 영역 -->
              <div style="position: absolute; left: 0; top: 0; width: calc(86% - 0.5px); height: 100%; background: #007AFF; clip-path: polygon(100% 0, 0 0, 0 100%, calc(100% - 26px) 100%);"></div>
              <!-- 회색 영역 (우측) -->
              <div style="position: absolute; right: 0; top: 0; width: 16%; height: 100%; background: #EEF3F6; clip-path: polygon(100% 0, 26px 0, 0 100%, 100% 100%)"></div>
            </div>

            <div style="padding: 20mm 20mm 20mm 20mm; letter-spacing: -0.03em;">
              <!-- 9. 근로계약서 교부 -->
              <section style="margin-bottom: 28px;">
                <div style="font-size: 13px; font-weight: 700; margin: 0 0 10px 0; letter-spacing: -0.3px; color: #000;">${
                  sectionLabels.delivery
                }. 근로계약서 교부</div>
                <p style="font-size: 12px; margin: 0; line-height: 1.8; color: #383E44; font-weight: 400; letter-spacing: -0.3px;">
                  사업주는 근로계약을 체결함과 동시에 본 계약서를 사본하여 근로자의
                  교부요구와 관계없이 근로자에게 교부함 (근로기준법 제 17조 이행)
                </p>
              </section>

              <!-- 10. 기타 -->
              <section>
                <div style="font-size: 13px; font-weight: 700; margin: 0 0 10px 0; letter-spacing: -0.3px; color: #000;">${
                  sectionLabels.etc
                }. 기타</div>
                <p style="font-size: 12px; margin: 0; line-height: 1.8; color: #383E44; font-weight: 400; letter-spacing: -0.3px;">이 계약에 정함이 없는 사항은 근로기준법령에 의함</p>
              </section>

              <!-- 서명란 -->
              <div style="margin-top: 60px; border-top: 1px solid #EEF3F6;"></div>
                <div style="text-align: center; margin: 60px 0 40px 0; font-size: 14px; color: #000; letter-spacing: -0.49px; font-weight: 600;">${formatDate(
                  data?.contractDate,
                )}</div>

                <div style="display: flex; flex-direction: column; gap: 40px;">
                  <div style="flex: 1; padding: 20px; border-radius: 8px; display: flex; flex-direction: row; gap: 10px; align-items: flex-start;">
                    <span style="font-size: 12px; letter-spacing: -0.3px; font-weight: 400; flex-shrink: 0;">(사업주)</span>
                    <div style="font-size: 12px; gap: 4px; position: relative; letter-spacing: -0.3px; font-weight: 400;">
                      <p>사업체명 : ${companyName} (전화: ${companyPhone})</p>
                      <p style="margin: 4px 0;">주소 : ${companyAddress}</p>
                      <p style="margin: 4px 0; position: relative;">
                        대표자 : ${representativeName} (서명)
                        <img src="/assets/seal.png" style="position: absolute; left: 70px; top: -10px; width: 60px; height: 60px; display: block;" />
                      </p>
                    </div>
                  </div>
                  <div style="flex: 1; padding: 20px; border-radius: 8px; display: flex; flex-direction: row; gap: 10px; align-items: flex-start;">
                    <span style="font-size: 12px; letter-spacing: -0.3px; font-weight: 400; flex-shrink: 0;">(근로자)</span>
                    <div style="font-size: 12px; gap: 4px; letter-spacing: -0.3px; font-weight: 400;">
                      <p>주소 : ${data?.employeeAddress || '____'}</p>
                      <p style="margin: 4px 0;">연락처 : ${data?.employeePhone || '____'}</p>
                      <p style="margin: 4px 0;">주민등록번호 : ${data?.employeeRRN || '____'}</p>
                      <p style="margin: 4px 0;">성명 : ${
                        data?.employeeName || '____'
                      } (서명)</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- 하우투약 푸터 -->
            <div style="position: absolute; bottom: 20mm; left: 0; right: 0; display: flex; justify-content: center; align-items: center; gap: 8px; font-size: 10px; color: #383E44;">
              <img src="/assets/icon/aops/howtoyak-logo.png" style="height: 16px; width: auto; display: block;" />
              <span style="font-weight: 600; letter-spacing: 0.08em;">HOW TO YAK</span>
            </div>
          </div>
        </body>
      </html>
    `
}

// 공통 PDF 생성 로직
const createPdfFromHtml = async (htmlContent) => {
  const iframe = document.createElement('iframe')
  iframe.style.position = 'absolute'
  iframe.style.left = '-9999px'
  iframe.style.width = '210mm'
  iframe.style.height = '800mm' // 두 페이지를 포함할 수 있도록 높이 증가
  iframe.style.border = 'none'
  document.body.appendChild(iframe)

  const iframeDoc = iframe.contentDocument || iframe.contentWindow.document
  iframeDoc.open()
  iframeDoc.write(htmlContent)
  iframeDoc.close()

  await new Promise((resolve) => setTimeout(resolve, 2000))

  const body = iframeDoc.body

  let checkCount = 0
  await new Promise((resolve) => {
    const checkRender = () => {
      checkCount++
      const pages = body.querySelectorAll('div')
      const hasContent = body.textContent && body.textContent.trim().length > 0
      const hasHeight = body.offsetHeight > 0

      if (hasContent && hasHeight && pages.length >= 2) {
        setTimeout(resolve, 500)
      } else if (checkCount > 100) {
        console.warn('렌더링 확인 실패, 계속 진행합니다.')
        console.log('body 높이:', body.offsetHeight)
        console.log('body 내용:', body.textContent?.substring(0, 100))
        console.log('div 개수:', pages.length)
        resolve()
      } else {
        setTimeout(checkRender, 50)
      }
    }
    checkRender()
  })

  const options = {
    margin: [0, 0, 0, 0],
    image: { type: 'jpeg', quality: 0.9 },
    html2canvas: {
      scale: 2,
      useCORS: true,
      letterRendering: true,
      logging: false,
      allowTaint: true,
      windowHeight: body.scrollHeight || 3000, // body의 실제 높이 사용
      height: body.scrollHeight || 3000, // body의 실제 높이 사용
      scrollY: 0, // 스크롤 위치 초기화
    },
    jsPDF: {
      unit: 'mm',
      format: 'a4',
      orientation: 'portrait',
    },
    // pagebreak: { mode: ['css', 'legacy'], avoid: [] },
    pagebreak: { mode: ['legacy'], avoid: ['section', 'p'], margin: 0 },
  }

  // body 전체를 캡처하여 두 페이지 모두 포함되도록 함
  const pdfBlob = await html2pdf().set(options).from(body).outputPdf('blob')

  document.body.removeChild(iframe)

  return pdfBlob
}

/**
 * 근로계약서 PDF 생성
 * @param {Object} data - 계약서 데이터
 * @returns {Promise<Blob>}
 */
export const generateLaborContractPdf = async (data) => {
  const htmlContent = buildLaborContractHtml(data)

  try {
    return await createPdfFromHtml(htmlContent)
  } catch (error) {
    console.error('근로계약서 PDF 생성 오류:', error)
    throw error
  }
}
